import {
    useRepayLoanTransaction,
    useAbfFetches,
    AbfLoanExpanded,
    LoopRouteResponseExpanded,
    useActiveWallet,
    LoanRepayType,
    getZcLoanCollateral,
    LoanCollateralExpanded
} from "@bridgesplit/abf-react";
import { LOADING_ERROR, MISSING_WALLET_ERROR, Result, uiAmountToLamports } from "@bridgesplit/utils";
import { TrackTransactionEvent } from "app/types";
import { RepayLoanTxnParams, WithdrawCollateralParams } from "@bridgesplit/abf-sdk";
import { trackSubmitRepay } from "app/hooks";

import { useTransactionSender } from "../../../transactions";
import { MakeRepaymentParams } from "./types";

export function useRepay(props: MakeRepaymentParams) {
    const {
        loanExpanded,
        principalPaymentAmount,
        isFullRepay,
        collateralWithdrawalParams,
        cpiIxs,
        cpiLuts,
        cpiSigners,
        mintToReceive,
        isLoopUnwind
    } = props;
    const send = useTransactionSender();
    const { resetLoanApi, resetMarketsPublicApi } = useAbfFetches();

    function resetRepay() {
        resetLoanApi(); // reset loan state
        resetMarketsPublicApi(); // reset orderbook quotes to include repaid amount
    }

    const { activeWallet } = useActiveWallet();

    const repay = useRepayLoanTransaction(isLoopUnwind);
    const collateral = getZcLoanCollateral(loanExpanded);

    async function repayLoan() {
        if (!loanExpanded) return Result.errFromMessage(LOADING_ERROR);
        if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);
        if (!collateral) return Result.errFromMessage(LOADING_ERROR);
        if (!mintToReceive) return Result.errFromMessage(LOADING_ERROR);
        if (!collateralWithdrawalParams) return Result.errFromMessage(LOADING_ERROR);

        const params: RepayLoanTxnParams = {
            loan: loanExpanded.loan.address,
            repayParams: [
                {
                    amount: uiAmountToLamports(principalPaymentAmount, loanExpanded.principalMetadata.decimals),
                    ledgerIndex: 0, // TODO: update when multiple ledgers are supported
                    repayAll: isFullRepay
                }
            ],
            collateralWithdrawalParams: collateralWithdrawalParams?.map((param) => ({
                ...param,
                amount: uiAmountToLamports(param.amount, collateral.metadata.decimals)
            })),
            cpiIxs,
            cpiLuts,
            cpiSigners,
            mintToReceive,
            userPublicKey: activeWallet
        };

        return await send(repay, params, {
            description: "Repaying loan",
            sendOptions: { refetch: () => resetRepay() },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitRepay,
                params: trackSubmitRepay(params, loanExpanded.principalMetadata) // TODO CHECK IF ARRAY IS ACCEPTABLE
            }
        });
    }

    return {
        repay: () => repayLoan()
    };
}

export function createRepaymentParams({
    principalPaymentUiAmount,
    collateralPaymentUiAmount,
    isFullRepay,
    repayType,
    collateral,
    loanExpanded,
    quote,
    isLoopUnwind
}: {
    principalPaymentUiAmount: number | undefined; // ui amount
    collateralPaymentUiAmount: number | undefined; // ui amount
    isFullRepay: boolean;
    repayType: LoanRepayType;
    collateral: LoanCollateralExpanded | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    quote: LoopRouteResponseExpanded | undefined;
    isLoopUnwind?: boolean;
}): MakeRepaymentParams {
    const cpiIxs = quote?.swapIxs;
    const cpiLuts = quote?.swapLuts;
    const cpiSigners = quote?.swapSigners;

    const receiveToken =
        repayType === LoanRepayType.Collateral ? collateral?.assetMint : loanExpanded?.principalMetadata.mint;

    return {
        loanExpanded,
        principalPaymentAmount: principalPaymentUiAmount, // ui amount
        isFullRepay,
        cpiIxs: cpiIxs ?? [],
        cpiLuts: cpiLuts ?? [],
        cpiSigners: cpiSigners ?? [],
        collateralWithdrawalParams: getCollateralWithdrawalParams({
            collateral,
            isFullRepay,
            collateralPaymentUiAmount,
            repayType,
            isLoopUnwind
        }),
        mintToReceive: receiveToken,
        isLoopUnwind: isLoopUnwind ?? false
    };
}

function getCollateralWithdrawalParams({
    collateral,
    isFullRepay,
    collateralPaymentUiAmount,
    isLoopUnwind,
    repayType
}: {
    collateral: LoanCollateralExpanded | undefined;
    isFullRepay: boolean;
    collateralPaymentUiAmount: number | undefined;
    repayType: LoanRepayType;
    isLoopUnwind: boolean | undefined;
}): WithdrawCollateralParams[] | undefined {
    if (!collateral) return undefined;

    const includeCollateralWithdrawal = repayType === LoanRepayType.Collateral || isLoopUnwind || isFullRepay;

    if (!includeCollateralWithdrawal) return [];

    return [
        {
            collateralMint: collateral.assetMint,
            amount: isFullRepay ? collateral.amount : collateralPaymentUiAmount ?? 0,
            closeIfEligible: isFullRepay
        }
    ];
}
